import { Show, createSignal, createEffect, onCleanup } from "solid-js";
import { Text } from "./i18n";
import { organization } from "../lib/auth-client";

export type SlugStatus = "idle" | "checking" | "available" | "taken";

/**
 * Normalize a string to be used as a slug
 * - Lowercase
 * - Remove accents/diacritical marks
 * - Replace spaces with hyphens
 */
export function normalizeSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks
    .replace(/\s+/g, "-");
}

/**
 * Hook to check if a slug is available
 * @param getSlug - Accessor function that returns the current slug
 * @param originalSlug - Optional original slug to skip checking (for edit mode)
 */
export function useSlugCheck(getSlug: () => string, originalSlug?: string) {
  const [slugStatus, setSlugStatus] = createSignal<SlugStatus>("idle");
  let checkSlugTimeout: ReturnType<typeof setTimeout> | null = null;

  createEffect(() => {
    const currentSlug = getSlug();

    if (checkSlugTimeout) {
      clearTimeout(checkSlugTimeout);
      checkSlugTimeout = null;
    }

    // If it's the original slug (edit mode), it's always valid
    if (originalSlug && currentSlug === originalSlug) {
      setSlugStatus("idle");
      return;
    }

    if (!currentSlug || currentSlug.length < 2) {
      setSlugStatus("idle");
      return;
    }

    setSlugStatus("checking");
    checkSlugTimeout = setTimeout(async () => {
      try {
        const { data } = await organization.checkSlug({ slug: currentSlug });
        // data is true if slug is available, false if taken
        setSlugStatus(data ? "available" : "taken");
      } catch {
        setSlugStatus("idle");
      }
    }, 300);
  });

  onCleanup(() => {
    if (checkSlugTimeout) {
      clearTimeout(checkSlugTimeout);
    }
  });

  return slugStatus;
}

interface SlugInputProps {
  value: string;
  onInput: (value: string) => void;
  slugStatus: SlugStatus;
  placeholder?: string;
  size?: "sm" | "md";
  class?: string;
}

export const SlugInput = (props: SlugInputProps) => {
  const inputClass = () => {
    const base = props.size === "sm" ? "ui-input ui-input-sm" : "ui-input";
    const error = props.slugStatus === "taken" ? "ui-input-error" : "";
    return `${base} ${error} ${props.class || ""}`.trim();
  };

  const handleInput = (e: InputEvent & { currentTarget: HTMLInputElement }) => {
    // Normalize the slug on input (lowercase, remove accents)
    const normalized = normalizeSlug(e.currentTarget.value);
    props.onInput(normalized);
  };

  return (
    <div class="flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <input
          type="text"
          class={inputClass()}
          value={props.value}
          onInput={handleInput}
          placeholder={props.placeholder}
        />
        <span class="flex h-5 w-5 shrink-0 items-center justify-center">
          <Show when={props.slugStatus === "checking"}>
            <span class="ui-loading ui-loading-spinner ui-loading-sm" />
          </Show>
          <Show when={props.slugStatus === "available"}>
            <span class="text-success text-sm">✓</span>
          </Show>
          <Show when={props.slugStatus === "taken"}>
            <span class="text-error text-sm">✗</span>
          </Show>
        </span>
      </div>
      <Show when={props.slugStatus === "taken"}>
        <span class="text-error text-sm">
          <Text message="This slug is already taken" />
        </span>
      </Show>
    </div>
  );
};
